<script setup lang="ts">
import { ProfileAdministrationAPI } from "../api";
import ParticipantsTable from "../components/participants/ParticipantsTable.vue";
import SupportSearchBar from "../components/SupportSearchBar.vue";
import { onMounted, ref, type Ref } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { useToast } from "primevue/usetoast";
import { Participant } from "../types/PersologOnlineTypes.ts";

const { t } = useI18n();
const toast = useToast();

const email = ref("");
const participants: Ref<Participant[]> = ref([]);
const loading = ref(false);
const searched = ref(false);
const missingIds = ref<number[]>([]);

/**
 * The participant ids in the search, when it is a list of ids instead of an e-mail (RDT-497): support pastes
 * the ids a query turned up, separated by commas, semicolons or spaces. Not by line breaks: the search is a
 * single-line input, which drops them, so a pasted column of ids would run together.
 */
const participantIds = (search: string): number[] | null =>
    /^[\d\s,;]+$/.test(search) && /\d/.test(search) ? [...new Set(search.split(/[\s,;]+/).filter(Boolean).map(Number))] : null;

const fetchParticipants = async () => {
    loading.value = true;
    try {
        const ids = participantIds(email.value.trim());
        participants.value = ids
            ? await ProfileAdministrationAPI.fetchParticipantsByIds(ids)
            : await ProfileAdministrationAPI.fetchParticipants(email.value);
        missingIds.value = ids?.filter((id) => !participants.value.some((p) => p.id === id)) ?? [];
    } catch (error) {
        // A failed search used to leave an empty page behind, which reads like "no such participant".
        participants.value = [];
        missingIds.value = [];
        console.error("Could not load the participants", error);
        toast.add({
            severity: "error",
            summary: t("messages.error"),
            detail: t("messages.load.error"),
            life: 4000,
        });
    } finally {
        loading.value = false;
        searched.value = true;
    }
};

onMounted(() => {
    const emailValue = useRoute().query.email;
    if (emailValue) {
        email.value = emailValue.toString();
        fetchParticipants();
    }
});
</script>

<template>
    <h1 class="page-title">{{ t("participants.title") }}</h1>
    <div class="pt-8 pb-8">
        <SupportSearchBar
            v-model="email"
            :placeholder="t('participants.searchEmailOrIds')"
            :hint="t('projects.input.instruction')"
            :loading="loading"
            @search="fetchParticipants"
        />
    </div>
    <ProgressSpinner v-if="loading" style="width: 2rem; height: 2rem" stroke-width="8" />
    <template v-else>
        <Message v-if="searched && !participants.length" severity="info" :closable="false">
            {{ t("participants.noResults") }}
        </Message>
        <Message v-if="missingIds.length" severity="warn" :closable="false" data-cy="participants-missing-ids">
            {{ t("participants.missingIds", { ids: missingIds.join(", ") }) }}
        </Message>
        <ParticipantsTable :participants="participants" @reload="fetchParticipants" />
    </template>
</template>
